import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, message as antMessage, Spin, Tabs } from 'antd'
import { useLanguage } from '../../../components/context/LanguageContext'
import { apiDownloadRequest, apiRequest } from '../../../helpers/apiRequest'
import JSONPretty from "react-json-pretty";
import { ReloadOutlined } from '@ant-design/icons'
import {
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaFileExcel,
  FaFileAlt,
  FaFilePowerpoint,
  FaFileArchive,
  FaFileVideo,
  FaFileAudio,
} from 'react-icons/fa'

const IMAGE_EXTENSIONS = new Set([
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.bmp',
  '.tiff',
  '.tif',
  '.webp',
  '.svg',
  '.heic',
])
const PDF_EXTENSIONS = new Set(['.pdf'])
const WORD_EXTENSIONS = new Set(['.doc', '.docx', '.rtf'])
const EXCEL_EXTENSIONS = new Set(['.xls', '.xlsx', '.csv'])
const POWERPOINT_EXTENSIONS = new Set(['.ppt', '.pptx'])
const ARCHIVE_EXTENSIONS = new Set(['.zip', '.rar', '.7z', '.tar', '.gz'])
const AUDIO_EXTENSIONS = new Set(['.mp3', '.wav', '.aac', '.flac', '.ogg'])
const VIDEO_EXTENSIONS = new Set(['.mp4', '.webm', '.mov', '.avi', '.mkv'])

const EmailMessageDetails = ({ message }) => {
  const languageContext = useLanguage()
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [resendToERP, setResendToERP] = useState(false)


  useEffect(() => {
    let isCancelled = false

    const fetchDetails = async () => {
      if (!message?.id) {
        setDetails(null)
        setHasError(false)
        setLoading(false)
        return
      }

      setLoading(true)
      setHasError(false)

      const endpoint = `${process.env.REACT_APP_API}emails/${message.id}`
      const response = await apiRequest(endpoint, 'GET')

      if (isCancelled) {
        return
      }

      if (response?.ok) {
        setDetails(response.data ?? null)
        setHasError(false)
      } else {
        setDetails(null)
        setHasError(true)
      }

      setLoading(false)
    }

    fetchDetails()

    return () => {
      isCancelled = true
    }
  }, [message?.id, message?.processedAt, message?.status])

  const data = useMemo(() => details || message, [details, message])

  const translate = useCallback((key, fallback) => {
    const value = languageContext.t(key)
    if (!value || value.startsWith('[')) {
      return fallback ?? key
    }
    return value
  }, [languageContext])

  const attachments = useMemo(() => {
    const raw = data?.metadata?.attachments
    return Array.isArray(raw) ? raw : []
  }, [data])

  const rawHtmlContent = useMemo(() => {
    if (!data) {
      return ''
    }
    if (data.bodyHtml) {
      return data.bodyHtml
    }
    if (data.body) {
      return data.body.replace(/\n/g, '<br />')
    }
    return ''
  }, [data])

  const textContent = useMemo(() => {
    if (!data) {
      return ''
    }
    if (data.body) {
      return data.body
    }
    if (!rawHtmlContent) {
      return ''
    }
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(rawHtmlContent, 'text/html')
      return doc.body.textContent || ''
    } catch (_error) {
      return rawHtmlContent.replace(/<[^>]+>/g, '')
    }
  }, [data, rawHtmlContent])

  const sanitizeExternalSources = useCallback(html => {
    if (!html) {
      return ''
    }

    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')

      const isExternalUrl = url => {
        if (!url) return false
        const trimmed = url.trim().toLowerCase()
        return (
          trimmed.startsWith('http://') ||
          trimmed.startsWith('https://') ||
          trimmed.startsWith('//')
        )
      }

      doc.querySelectorAll('[src]').forEach(element => {
        const src = element.getAttribute('src')
        if (isExternalUrl(src)) {
          element.removeAttribute('src')
        }
      })

      doc.querySelectorAll('a[href]').forEach(anchor => {
        const href = anchor.getAttribute('href')
        if (isExternalUrl(href)) {
          anchor.removeAttribute('href')
        }
      })

      return doc.body.innerHTML || ''
    } catch (_error) {
      return html
    }
  }, [])

  const sanitizedHtmlContent = useMemo(
    () => sanitizeExternalSources(rawHtmlContent),
    [rawHtmlContent, sanitizeExternalSources],
  )

  const renderAttachmentMeta = attachment => {
    const parts = []
    if (attachment?.contentType) {
      parts.push(attachment.contentType)
    }
    if (typeof attachment?.size === 'number') {
      parts.push(`${(attachment.size / 1024).toFixed(1)} KB`)
    }
    if (attachment?.isInline) {
      parts.push(translate('Inline', 'Inline'))
    }
    return parts.join(' · ')
  }

  const getAttachmentExtension = attachment => {
    if (!attachment) return ''
    const fromField =
      attachment.fileExtension ||
      attachment.fileName ||
      attachment.name ||
      ''
    if (typeof fromField === 'string' && fromField.includes('.')) {
      const extMatch = /(\.[^./\\]+)$/.exec(fromField)
      if (extMatch) {
        return extMatch[1].toLowerCase()
      }
    }
    return (attachment.fileExtension || '').toLowerCase()
  }

  const getAttachmentContentType = attachment =>
    (attachment?.contentType || '').toLowerCase()

  const isImageAttachment = useCallback(attachment => {
    const type = getAttachmentContentType(attachment)
    if (type.startsWith('image/')) return true
    const ext = getAttachmentExtension(attachment)
    return IMAGE_EXTENSIONS.has(ext)
  }, [])

  const isPreviewableAttachment = useCallback(attachment => {
    const type = getAttachmentContentType(attachment)
    if (!attachment) return false
    if (isImageAttachment(attachment)) return true
    if (type === 'application/pdf') return true
    const ext = getAttachmentExtension(attachment)
    if (PDF_EXTENSIONS.has(ext)) return true
    if (type.startsWith('text/')) return true
    if (['application/json'].includes(type)) return true
    return false
  }, [isImageAttachment])

  const getAttachmentIcon = attachment => {
    if (isImageAttachment(attachment)) {
      return <FaFileImage />
    }
    const type = getAttachmentContentType(attachment)
    const ext = getAttachmentExtension(attachment)
    if (type === 'application/pdf' || PDF_EXTENSIONS.has(ext)) {
      return <FaFilePdf />
    }
    if (WORD_EXTENSIONS.has(ext)) {
      return <FaFileWord />
    }
    if (EXCEL_EXTENSIONS.has(ext)) {
      return <FaFileExcel />
    }
    if (POWERPOINT_EXTENSIONS.has(ext)) {
      return <FaFilePowerpoint />
    }
    if (ARCHIVE_EXTENSIONS.has(ext)) {
      return <FaFileArchive />
    }
    if (AUDIO_EXTENSIONS.has(ext) || type.startsWith('audio/')) {
      return <FaFileAudio />
    }
    if (VIDEO_EXTENSIONS.has(ext) || type.startsWith('video/')) {
      return <FaFileVideo />
    }
    return <FaFileAlt />
  }

  const buildStoredAttachmentUrl = useCallback(
    (attachment, endpoint) => {
      if (
        attachment?.filePath &&
        attachment?.downloadType &&
        attachment?.collectionIndex != null &&
        data?.id
      ) {
        return (
          `${process.env.REACT_APP_API}emails/${data.id}/attachments/` +
          `${attachment.downloadType}/${attachment.collectionIndex}/${endpoint}`
        )
      }
      return null
    },
    [data?.id],
  )

  const buildDownloadUrl = useCallback(
    attachment =>
      buildStoredAttachmentUrl(attachment, 'download') ||
      attachment?.url ||
      null,
    [buildStoredAttachmentUrl],
  )

  const buildViewUrl = useCallback(
    attachment =>
      buildStoredAttachmentUrl(attachment, 'view') ||
      attachment?.url ||
      null,
    [buildStoredAttachmentUrl],
  )

  const openAttachment = useCallback(
    (attachment, event) => {
      if (event) {
        event.preventDefault()
      }

      if (!attachment) {
        return
      }

      const previewable = isPreviewableAttachment(attachment)
      const viewUrl = buildViewUrl(attachment)
      if (previewable && viewUrl) {
        window.open(viewUrl, '_blank', 'noopener,noreferrer')
        return
      }

      const downloadUrl = buildDownloadUrl(attachment)
      if (!downloadUrl) {
        return
      }

      const fileName =
        attachment.fileName ||
        attachment.name ||
        `${attachment.downloadType ?? 'file'}-${attachment.collectionIndex ?? '0'}`

      if (
        attachment?.filePath &&
        attachment?.downloadType &&
        attachment?.collectionIndex != null &&
        data?.id
      ) {
        apiDownloadRequest(downloadUrl, fileName, attachment.contentType)
      } else {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer')
      }
    },
    [buildDownloadUrl, buildViewUrl, data?.id, isPreviewableAttachment],
  )

  const handleReprocess = useCallback(async () => {
    if (!message?.id || processing) {
      return
    }

    setProcessing(true)
    const endpoint = `${process.env.REACT_APP_API}emails/${message.id}/process`

    try {
      const response = await apiRequest(endpoint, 'POST', {})

      if (response?.ok) {
        antMessage.success(
          translate(
            'Email reprocessed successfully',
            'Email reprocessed successfully',
          ),
        )
      } else {
        antMessage.error(
          translate(
            'Failed to reprocess email',
            'Failed to reprocess email',
          ),
        )
      }
    } catch (error) {
      antMessage.error(
        translate(
          'Error reprocessing email',
          'Error reprocessing email',
        ),
      )
    } finally {
      setProcessing(false)
    }

    const detailsEndpoint = `${process.env.REACT_APP_API}emails/${message.id}`
    const detailsResponse = await apiRequest(detailsEndpoint, 'GET')

    if (detailsResponse?.ok) {
      setDetails(detailsResponse.data ?? null)
    }
  }, [message?.id, processing, translate])

  const handleResendERP = useCallback(async () => {
    if (!message?.id || resendToERP) return
    setResendToERP(true)
    const endpoint = `${process.env.REACT_APP_API}emails/${message.id}/sendtoerp`
    try {
      const response = await apiRequest(endpoint, 'POST', {})
      if (response?.ok) {
        antMessage.success(
          translate('Email resent to ERP successfully', 'Email resent to ERP successfully')
        )
      } else {
        antMessage.error(
          translate('Failed to resend email to ERP', 'Failed to resend email to ERP')
        )
      }
    } catch (error) {
      antMessage.error(
        translate('Error resending email to ERP', 'Error resending email to ERP')
      )
    } finally {
      setResendToERP(false)
    }
    const erpEndpoint = `${process.env.REACT_APP_API}emails/${message.id}`
    const erpResponse = await apiRequest(erpEndpoint, 'GET')
    if (erpResponse?.ok) {
      setDetails(erpResponse.data ?? null)
    }
  }, [message?.id, resendToERP, translate])

  const attachmentStyles = useMemo(
    () => ({
      grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: 16,
        margin: 0,
        padding: 10,
      },
      card: {
        listStyle: 'none',
        background: '#ffffff',
        border: '1px solid #e9ecef',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 8px 16px -12px rgba(15, 23, 42, 0.45)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      },
      preview: {
        height: 200,
        background: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        overflow: 'hidden',
      },
      previewImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      },
      icon: {
        fontSize: 56,
        color: '#6c757d',
      },
      details: {
        padding: '14px 16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      },
      nameLink: {
        fontWeight: 600,
        fontSize: 15,
        color: '#0d6efd',
        textDecoration: 'none',
        wordBreak: 'break-word',
      },
      meta: {
        color: '#6c757d',
        fontSize: 12,
        lineHeight: 1.4,
      },
      stored: {
        color: '#198754',
        fontSize: 12,
      },
      error: {
        color: '#dc3545',
        fontSize: 12,
      },
    }),
    [],
  )

  if (!message?.id) {
    return null
  }

  return (
    <Card className="mt-3">
      {hasError && (
        <Alert
          type="error"
          message={translate('Error', 'Error')}
          description={translate(
            'Could not load email message details',
            'Could not load email message details',
          )}
          style={{ marginBottom: 16 }}
        />
      )}
      <Spin spinning={loading}>
        <Tabs
          defaultActiveKey="details"
          type="card"
          tabBarExtraContent={
            <>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={processing}
                onClick={handleReprocess}
                disabled={!message?.id || processing}
              >
                {translate('Reprocess Email', 'Reprocess Email')}
              </Button>
              <Button
                type="default"
                loading={processing}
                disabled={!message?.id || processing}
                onClick={handleResendERP}
                style={{ marginLeft: 8 }}
              >
                {translate('Resend to ERP', 'Resend to ERP')}
              </Button>
            </>
          }
        >
          <Tabs.TabPane tab={translate('Details', 'Details')} key="details">
            <div style={{ marginBottom: 12 }}>
              <strong>{translate('Subject', 'Subject')}:</strong>{' '}
              {data?.subject || '-'}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>{translate('From', 'From')}:</strong>{' '}
              {data?.from || '-'}
            </div>
            {data?.receivedAt && (
              <div style={{ marginBottom: 12 }}>
                <strong>{translate('Received At', 'Received At')}:</strong>{' '}
                {new Date(data.receivedAt).toLocaleString()}
              </div>
            )}
            <div>
              <strong>{translate('Content', 'Content')}:</strong>
              <Tabs
                size="small"
                defaultActiveKey="html"
                type="card"
                style={{ marginTop: 8 }}
              >
                <Tabs.TabPane tab={translate('HTML', 'HTML')} key="html">
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizedHtmlContent?.replace(/\n/g, '<br />') || '-',
                    }}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab={translate('Text', 'Text')} key="text">
                  <div
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {textContent || '-'}
                  </div>
                </Tabs.TabPane>
              </Tabs>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane
            tab={translate('Attachments', 'Attachments')}
            key="attachments"
          >
            {attachments.length > 0 ? (
              <div style={attachmentStyles.grid}>
                {attachments.map(attachment => {
                  const key =
                    attachment.id ||
                    `${attachment.name ?? attachment.fileName ?? 'attachment'}-${attachment.url ?? ''}`
                  const meta = renderAttachmentMeta(attachment)
                  const isStored =
                    attachment?.filePath &&
                    attachment?.downloadType &&
                    attachment?.collectionIndex != null &&
                    data?.id
                  const previewable = isPreviewableAttachment(attachment)
                  const downloadUrl = buildDownloadUrl(attachment)
                  const viewUrl = buildViewUrl(attachment)
                  const anchorHref =
                    (previewable && viewUrl) || downloadUrl || '#'
                  const displayName =
                    attachment.name ||
                    attachment.fileName ||
                    translate('Attachment', 'Attachment')
                  const icon = getAttachmentIcon(attachment)
                  const previewCursor =
                    previewable || downloadUrl ? 'pointer' : 'not-allowed'

                  return (
                    <div key={key} style={attachmentStyles.card}>
                      <div
                        style={{ ...attachmentStyles.preview, cursor: previewCursor }}
                        role="button"
                        tabIndex={previewable || downloadUrl ? 0 : -1}
                        onClick={event => openAttachment(attachment, event)}
                        onKeyDown={event => {
                          if (
                            event.key === 'Enter' ||
                            event.key === ' ' ||
                            event.key === 'Spacebar'
                          ) {
                            openAttachment(attachment, event)
                          }
                        }}
                      >
                        {isImageAttachment(attachment) && viewUrl ? (
                          <img
                            src={viewUrl}
                            alt={displayName}
                            style={attachmentStyles.previewImage}
                          />
                        ) : (
                          <div style={attachmentStyles.icon}>{icon}</div>
                        )}
                      </div>
                      <div style={attachmentStyles.details}>
                        <a
                          href={anchorHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={attachmentStyles.nameLink}
                          onClick={event => openAttachment(attachment, event)}
                        >
                          {displayName}
                        </a>
                        {meta && (
                          <div style={attachmentStyles.meta}>{meta}</div>
                        )}
                        {isStored && attachment.fileName && (
                          <div style={attachmentStyles.stored}>
                            {translate('Downloaded File', 'Downloaded file')}:{' '}
                            {attachment.fileName}
                          </div>
                        )}
                        {attachment.downloadError && (
                          <div style={attachmentStyles.error}>
                            {translate('Download Error', 'Download error')}:{' '}
                            {attachment.downloadError}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div style={{ color: '#6c757d' }}>
                {translate('No Attachments', 'No attachments available')}
              </div>
            )}
          </Tabs.TabPane>
          {/* {(data?.status === 'processed' || data?.status === 'sent' || data?.status === 'error-erp') && data?.response && ( */}
          {(data?.status !== 'pending') && data?.response && (
            <Tabs.TabPane
              tab={translate('Processing Results', 'Processing Results')}
              key="processing"
            >
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 16 }}>
                  <strong>{translate('AI Response', 'AI Response')}:</strong>
                  <div
                    style={{
                      marginTop: 8,
                      padding: 12,
                      background: '#f5f5f5',
                      borderRadius: 4,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                <JSONPretty data={data.response} />
                  </div>
                </div>
                {data.processingLog && (
                  <div>
                    <strong>{translate('Processing Log', 'Processing Log')}:</strong>
                    <div
                      style={{
                        marginTop: 8,
                        padding: 12,
                        background: '#fafafa',
                        borderRadius: 4,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: 12,
                        maxHeight: 400,
                        overflowY: 'auto',
                      }}
                    >
                      {data.processingLog}
                    </div>
                  </div>
                )}
              </div>
            </Tabs.TabPane>
          )}
          {/* {data?.status === 'sent' && data?.erpResponse && ( */}
          {data?.status !== 'pending' && data?.status !== 'error' && data?.erpResponse && (
            <Tabs.TabPane
              tab={translate('ERP Response', 'ERP Response')}
              key="erpResponse"
            >
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  background: '#f5f5f5',
                  borderRadius: 4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                <JSONPretty data={data.erpResponse} />
              </div>
            </Tabs.TabPane>
          )}
        </Tabs>
      </Spin>
    </Card>
  )
}

export default EmailMessageDetails
